/* eslint-disable no-unused-expressions */
import React, { useState } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import CustomPopupText from '../../shared/CustomPopupText';
import CustomTextField from '../../shared/CustomTextField';
import CustomPicker from '../../shared/CustomPicker';
import CustomUploadField from '../../shared/CustomUploadField';
import MultiSelectSearch from '../../common/search/MultiSelectSearch';
import CustomButtonGroup from '../../shared/CustomButtonGroup';
import { buttons, types } from '../constants';
import { CustomReactPlayer } from '../../common/ReactPlayer';
import ZeroState from '../../common/ZeroState';
import ExclamationIcon from '../../../assets/ExclamationIcon';
import CustomRadioGroup from '../../shared/CustomRadioGroup';
import CustomAutoComplete from '../../shared/CustomAutoComplete';
import { generateVideoUrl } from '../../utils/Utils';
import { DIMENSIONS } from '../../../constants/aspectRatio';
import { VideoLinkInput } from '../../../features/vimeo-picker';

const useStyle = makeStyles(() => ({
  popupMainContainer: {
    background: 'rgba(242, 244, 247, 1)',
    width: '100%',
  },
  publishDateField: {
    height: '44px',
    padding: '0px 12px',
    fontSize: '12px',
    margin: '12px 0px',
    border: '1px solid rgba(228, 231, 236, 1)',
    borderRadius: '8px',
    background: 'rgba(254, 254, 254, 1)',
  },

  section: {
    padding: '16px',
  },
}));

const skillLevelOptions = [
  { label: 'Beginner', value: 0 },
  { label: 'Intermediate', value: 1 },
  { label: 'Advanced', value: 2 },
];

const AddInfo = ({ data, setData, collectionList }) => {
  const classes = useStyle();
  const tagList = useSelector((state) => state.getTagList.taglist);
  const [certFileName, setCertFileName] = useState('');
  const [thumbnailFileName, setThumbnailFileName] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [thumbnailImg, setThumbnailImg] = useState(null);
  const [certificateImg, setCertificateImg] = useState(null);
  const handleChange = ({ target: { name, value } }) => {
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (label, date) => {
    setData((prev) => ({ ...prev, [label]: date }));
  };
  const handleTag = (event, value) => {
    const tempSelected = [];
    value.forEach((element) => {
      tempSelected?.push(element);
    });
    setData((prev) => ({ ...prev, tags: [...tempSelected] }));
  };
  const handleThumbnailUpload = (e, field) => {
    const file = e.target.files[0];
    field === 'certificateBg' ? setCertFileName(file?.name) : setThumbnailFileName(file?.name);
    setData((prev) => ({ ...prev, [field]: file }));
    const reader = new FileReader();
    reader.onload = (ev) => {
      field === 'thumbnail' ? setThumbnailImg(ev.target.result) : setCertificateImg(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleMultiSelect = (arr) => {
    setData((prev) => ({ ...prev, collections: arr }));
  };

  const handleSkillLevelChange = (index) => {
    const selected = skillLevelOptions[index].value;
    setData((prev) => ({ ...prev, skillLevel: selected }));
  };

  const handleDeleteImage = (type) => {
    if (type === 'certificate') {
      setData((prev) => ({ ...prev, certificateBg: null }));
      setCertificateImg(null);
      setCertFileName('');
    } else if (type === 'thumbnail') {
      setData((prev) => ({ ...prev, thumbnail: null }));
      setThumbnailImg(null);
      setThumbnailFileName('');
    }
  };

  return (
    <Box className={classes.popupMainContainer}>
      <Grid container>
        {/* Left Side */}
        <Grid item xs={6}>
          <Grid container direction="column" className={classes.section}>
            <Grid item>
              <CustomPopupText text="Journey Title" required />
              <CustomTextField
                name="journeyTitle"
                value={data?.journeyTitle}
                handleChange={handleChange}
                width="100%"
              />
            </Grid>

            <Grid item>
              <CustomPopupText text="Course Overview" required />
              <CustomTextField
                name="courseOverview"
                value={data?.courseOverview}
                handleChange={handleChange}
                width="100%"
                multiline
              />
            </Grid>

            <Grid item>
              <CustomPopupText text="Journey Goal" required />
              <CustomTextField
                name="journeyGoal"
                value={data?.journeyGoal}
                handleChange={handleChange}
                width="100%"
                multiline
                height="200px"
              />
            </Grid>

            <Grid item>
              <CustomPopupText text="Journey Reporting Code" />
              <CustomTextField
                name="courseCode"
                value={data?.courseCode}
                handleChange={handleChange}
                width="100%"
                multiline
                height="200px"
              />
            </Grid>

            <Grid item>
              <CustomPopupText text="Publish Date" required />
              <CustomPicker
                name="publishDate"
                data={data}
                handleDateChange={handleDateChange}
                customStyles={classes.publishDateField}
              />
            </Grid>

            <Grid item>
              <CustomPopupText text="Thumbnail (max 100kb)" required />
              <CustomUploadField
                type="image"
                name="thumbnail"
                value={thumbnailImg}
                imgSrc={data?.thumbnail}
                handleChange={(e) => handleThumbnailUpload(e, 'thumbnail')}
                customStyles={{ width: '100%' }}
                fileName={thumbnailFileName}
                dimensions={DIMENSIONS.journey}
                handleDelete={() => handleDeleteImage('thumbnail')}
                wordWrapWidth="180px"
              />
            </Grid>

            <Grid item>
              <CustomPopupText text="Certificate Background" />
              <CustomUploadField
                type="image"
                name="certificateBg"
                value={certificateImg}
                imgSrc={data?.certificateBg}
                handleChange={(e) => handleThumbnailUpload(e, 'certificateBg')}
                handleDelete={() => handleDeleteImage('certificate')}
                fileName={certFileName}
                customStyles={{ width: '100%' }}
                wordWrapWidth="180px"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Right Side */}
        <Grid item xs={6}>
          <Grid container direction="column" className={classes.section}>
            <Grid item>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <CustomPopupText text="Video Preview" />
                </Grid>
                <Grid item>
                  {data?.previewLink ? (
                    <CustomReactPlayer
                      url={
                        data?.avPreview?.url === data?.previewLink
                          ? generateVideoUrl(data?.avPreview)
                          : data?.previewLink
                      }
                      controls
                      width="100%"
                      height="100%"
                      onError={() => {
                        setData((prev) => ({ ...prev, previewLink: '' }));
                        setIsValidUrl(false);
                      }}
                    />
                  ) : (
                    <ZeroState
                      Icon={ExclamationIcon}
                      text={
                        isValidUrl
                          ? 'Add video link for the video preview to be visible'
                          : 'Not able to render the video. Please verify if video URL is valid.'
                      }
                      style={{ marginBottom: '30px', marginTop: '20px', width: '400px', height: '170px' }}
                      type="video"
                      iconColor={isValidUrl ? '#98A2B3' : '#D92D20'}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <VideoLinkInput
                value={data?.previewLink || ''}
                onChange={(val) => handleChange({ target: { name: 'previewLink', value: val } })}
                label="Journey Preview Link"
              />
            </Grid>
            <Grid item>
              <Grid container direction="column" spacing={1} style={{ marginBottom: '10px' }}>
                <Grid item>
                  <CustomPopupText text="Collections" />
                </Grid>
                <Grid item>
                  <MultiSelectSearch
                    placeholderButtonLabel="Select a Collection"
                    selectedOptions={data?.collections}
                    onChange={handleMultiSelect}
                    options={collectionList || []}
                    width="100%"
                    maxMenuHeight={150}
                    labelWidth="270px"
                    white
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <CustomPopupText text="Skill Level" required />
                </Grid>
                <Grid item>
                  <CustomButtonGroup
                    buttons={buttons}
                    handleChange={handleSkillLevelChange}
                    selectedIndex={data?.skillLevel}
                    icon={false}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <CustomPopupText text="Is it OEM content?" required />
              <CustomRadioGroup name="isOEM" value={data?.isOEM} options={types} handleChange={handleChange} />
            </Grid>
            {data?.isOEM === '1' ? (
              <Grid item>
                <CustomPopupText text="Due Date" required />
                <CustomPicker
                  name="dueDate"
                  data={data}
                  handleDateChange={handleDateChange}
                  customStyles={classes.publishDateField}
                />
              </Grid>
            ) : null}
            <Grid item>
              <CustomPopupText text="Journey Tags" />
              <CustomAutoComplete list={tagList || []} value={data?.tags} handleChange={handleTag} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddInfo;
